'use client'

import { useAuthState } from '@/app/providers/auth'
import { useAppError } from '@/components/error-page/errors'
import { lookupAddressDetails } from '@/components/page-specific/catering/common/helpers/addresses'
import { userStateDateTimeToUTCString } from '@/components/page-specific/catering/common/helpers/date'
import {
  getVendorSearchDateTime,
  normalizeUtcDateTimeString,
} from '@/components/page-specific/catering/suppliers/utils/date'
import { graphql } from '@/generated/gql'
import {
  type InputMaybe,
  ServiceType,
  type VendorMarketplaceOrderByInput,
  VendorMarketplaceOrderByProperties,
  VendorOrderDirection,
  VendorsInRangeQuery,
  VendorsInRangeQueryVariables,
} from '@/generated/gql/graphql'
import {
  type Address,
  type TenantFromPrebuildFragment,
  type UserBasketSettingsLocationFragment,
} from '@/generated/graphql'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import useTenant from '@/hooks/useTenant'
import { useApolloClient, useLazyQuery } from '@apollo/client'
import { basketLocationToAddress } from '@caterdesk/utils--gm-validation'

import { debounce } from 'lodash'
import { ParsedUrlQueryInput } from 'querystring'
import { useEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'

import { useAppStateStore } from '../../common/store/appStateStore'
import useUrlSearchFilters from './useUrlSearchFilters'

const findVendorsInRangeQuery = graphql(/* GraphQL */ `
  query VendorsInRange(
    $offset: Int
    $search: String
    $location: Coordinates
    $lat: Float!
    $lng: Float!
    $headCount: Int
    $keyDatesTags: [String]
    $cuisineTags: [String]
    $occasionTags: [String]
    $dietaryTags: [String!]
    $corporateTags: [String!]
    $highlightsTags: [String]
    $diversityOfOwnershipTags: [String!]
    $bestForTags: [String!]
    $minDateTime: DateTime
    $tenantId: String
    $orderBy: VendorMarketplaceOrderByInput
    $serviceType: ServiceType!
    $maxRating: Int
    $queryVersion: Int
    $dateTime: VendorListDateSearchInput
    $deliveryRegionFilter: DeliveryRegionInput
    $supplierType: String
    $itemSearch: String
  ) {
    vendors(
      filters: {
        isGMActive: true
        isActive: true
        isDraft: false
        getStats: true
        serviceType: $serviceType
        search: $search
        location: $location
        headCount: $headCount
        corporateTags: $corporateTags
        keyDatesTags: $keyDatesTags
        cuisineTags: $cuisineTags
        highlightsTags: $highlightsTags
        diversityOfOwnershipTags: $diversityOfOwnershipTags
        occasionTags: $occasionTags
        dietaryTags: $dietaryTags
        bestForTags: $bestForTags
        minDateTime: $minDateTime
        tenantId: $tenantId
        dateTime: $dateTime
        supplierType: $supplierType
        itemSearch: $itemSearch
      }
      offset: $offset
      orderBy: $orderBy
      queryVersion: $queryVersion
    ) {
      pageInfo {
        hasMore
        offset
        limit
        count
      }
      stats {
        occasionTags {
          id
          label
          name
          position
          count
        }
        dietaryTags {
          id
          label
          name
          position
          count
        }
        keyDatesTags {
          id
          label
          name
          position
          count
        }
        diversityOfOwnershipTags {
          id
          label
          name
          position
          count
        }
        cuisineTags {
          id
          label
          name
          position
          count
        }
        bestForTags {
          id
          label
          name
          position
          count
        }
      }
      rows {
        id
        permalink
        companyName
        currency
        locations {
          isDefault
          deliveryRegions(input: $deliveryRegionFilter) {
            serviceType
            orderPosition
            minOrderValueExTax
            deliveryFee {
              totalExTax
            }
          }
          serviceTimes {
            mealType
          }
        }
        images {
          secureUrl
          position
        }
        logo
        priceLevel
        descriptionShort
        keywords
        rating(input: { maxRating: $maxRating })
        totalRatings
        gmRating
        gmTotalRatings
        tags(filters: { groupId: 47 }) {
          name
          label
          groupId
        }
        accountIdsWithPrivateItemAccess
        isApprovedSupplier
        isMyFavourite
        itemSearchMetadata {
          rank
          reason
          items {
            id
            name
          }
        }
        translations {
          language
          key
          value
        }
        inRangeLocations(lat: $lat, lng: $lng, serviceType: $serviceType, dateTime: $dateTime) {
          id
          country
          details {
            minOrderValue
            minOrderValueExTax
            gmMaxDistanceMinutes
            gmMinNoticePeriod
            gmMaxOrder
            gmLargeOrderThreshold
            gmLargeOrderNoticePeriod
            isGMActive
            gmAllowCountryWideDelivery
            effectiveMinimumNoticePeriod
          }
          closingDates {
            closedWeekdays
            closedDates
          }
          openingTimes {
            startTime
            endTime
            weekdays
          }
          deliveryRegions(input: $deliveryRegionFilter) {
            id
            minOrderValue
            minOrderValueExTax
            orderPosition
            serviceType
            deliveryFee {
              total
              totalExTax
              taxRate
            }
          }
        }
      }
    }
  }
`)

export type VendorSearchState = {
  pageInfo?: VendorsInRangeQuery['vendors']['pageInfo']
  vendors: VendorsInRangeQuery['vendors']['rows']
  stats: VendorsInRangeQuery['vendors']['stats']
  searching: boolean
  searchComplete: boolean
  called: boolean
  search: (query: Partial<VendorsInRangeQueryVariables>) => void
  toggleFilterOpen: () => void
  filterOpen: boolean
  filters: Partial<VendorsInRangeQueryVariables>
  filterCount: number
  defaultSearch: () => void
}

export type TagGroupNames = {
  id: string
  key:
    | 'cuisineTags'
    | 'dietaryTags'
    | 'keyDatesTags'
    | 'occasionTags'
    | 'diversityOfOwnershipTags'
    | 'bestForTags'
  label: string
}
export const tagGroupNames: Record<string, TagGroupNames> = {
  Cuisine: {
    id: '4',
    key: 'cuisineTags',
    label: 'Cuisine',
  },
  Dietary: {
    id: '1',
    key: 'dietaryTags',
    label: 'Dietary',
  },
  KeyDates: {
    id: '16',
    key: 'keyDatesTags',
    label: 'Key dates',
  },
  Occasion: {
    id: '3',
    key: 'occasionTags',
    label: 'Occasion',
  },
  DiversityOfOwnership: {
    id: '21',
    key: 'diversityOfOwnershipTags',
    label: 'Diversity of ownership',
  },
  BestFor: {
    id: '10',
    key: 'bestForTags',
    label: 'Best for',
  },
}

const routerQuerySchemaWithDefaults = z.object({
  search: z.string().default(''),
  mealTypes: z
    .preprocess((val) => {
      return typeof val === 'string' ? [val] : val || []
    }, z.string().array())
    .default([]),
  headCountMin: z.string().optional(),
  headCountMax: z.string().optional(),
  headCount: z.preprocess((val) => {
    if (typeof val === 'string' || typeof val === 'number') {
      const num = Number(val)
      return Number.isNaN(num) ? undefined : num
    }
    return undefined
  }, z.number().optional().default(0)),
  priceLevelRange: z
    .preprocess((val) => {
      if (!val) return []
      if (typeof val === 'string') {
        const parsedNum = Number(val)
        if (parsedNum > 3 || parsedNum < 1) return []
        return [parsedNum]
      }
      return (val as string[]).reduce((acc: number[], v: string) => {
        const parsedNum = Number(v)
        if (parsedNum > 3 || parsedNum < 1) return acc
        return [...acc, parsedNum]
      }, [])
    }, z.number().min(1).max(3).array())
    .default([]),
  keyDatesTags: z
    .preprocess((val) => {
      return typeof val === 'string' ? [val] : val || []
    }, z.string().array())
    .default([]),
  cuisineTags: z
    .preprocess((val) => {
      return typeof val === 'string' ? [val] : val || []
    }, z.string().array())
    .default([]),
  diversityOfOwnershipTags: z
    .preprocess((val) => {
      return typeof val === 'string' ? [val] : val || []
    }, z.string().array())
    .default([]),
  occasionTags: z
    .preprocess((val) => {
      return typeof val === 'string' ? [val] : val || []
    }, z.string().array())
    .default([]),
  highlightsTags: z
    .preprocess((val) => {
      return typeof val === 'string' ? [val] : val || []
    }, z.string().array())
    .default([]),
  dietaryTags: z
    .preprocess((val) => {
      return typeof val === 'string' ? [val] : val || []
    }, z.string().array())
    .default([]),
  corporateTags: z
    .preprocess((val) => {
      return typeof val === 'string' ? [val] : val || []
    }, z.string().array())
    .default([]),
  bestForTags: z
    .preprocess((val) => {
      return typeof val === 'string' ? [val] : val || []
    }, z.string().array())
    .default([]),
  lat: z
    .preprocess((val) => Number(val), z.number())
    .optional()
    .catch(undefined),
  lng: z
    .preprocess((val) => Number(val), z.number())
    .optional()
    .catch(undefined),
  postcode: z.string().optional(),
  street: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
  minDateTime: z.preprocess(
    (val) => normalizeUtcDateTimeString(val as string | null | undefined),
    z.string().optional().default(''),
  ),
  orderByDirection: z.string().optional().default('asc'),
  orderByProperty: z.string().optional().default('distance'),
  supplierType: z
    .preprocess(
      (val) => (typeof val === 'string' ? val.replace(/\?.*$/, '') : val),
      z.enum(['pantry', 'catering', 'all']),
    )
    .optional()
    .default('catering')
    .catch('catering'),
  itemSearch: z.string().optional(),
})

const mapUrlFiltersToGraphqlFilters = (
  parsed: z.infer<typeof routerQuerySchemaWithDefaults>,
  tenantState: TenantFromPrebuildFragment,
  fiveStarRating: boolean,
): VendorsInRangeQueryVariables => {
  const validParsedDate = normalizeUtcDateTimeString(parsed.minDateTime) || null

  const coordinates =
    parsed.lat != null && parsed.lng != null
      ? { latitude: parsed.lat, longitude: parsed.lng }
      : null
  return {
    search: parsed.search ?? '',
    headCount: parsed.headCount ?? 0,
    keyDatesTags: parsed.keyDatesTags ?? null,
    cuisineTags: parsed.cuisineTags ?? null,
    diversityOfOwnershipTags: parsed.diversityOfOwnershipTags ?? null,
    occasionTags: parsed.occasionTags ?? null,
    dietaryTags: parsed.dietaryTags ?? null,
    bestForTags: parsed.bestForTags ?? null,
    highlightsTags: parsed.highlightsTags ?? null,
    corporateTags: parsed.corporateTags ?? null,
    supplierType: parsed.supplierType ?? null,
    location: coordinates,
    lat: coordinates?.latitude ?? 0,
    lng: coordinates?.longitude ?? 0,
    orderBy: parsed.orderByProperty
      ? ({
          property: parsed.orderByProperty,
          direction: parsed.orderByDirection || 'asc',
        } as VendorMarketplaceOrderByInput)
      : null,
    deliveryRegionFilter: {
      serviceType: ServiceType.Gm,
      ...(coordinates && {
        location: { lat: coordinates.latitude, lng: coordinates.longitude },
      }),
    },
    minDateTime: validParsedDate,
    dateTime: getVendorSearchDateTime(validParsedDate),
    serviceType: ServiceType.Gm,
    tenantId: tenantState ? tenantState.id : null,
    queryVersion: 2,
    offset: 0,
    maxRating: fiveStarRating ? 5 : 4,
    itemSearch: parsed.itemSearch ?? undefined,
  }
}

const cleanTagArray = (
  input?: InputMaybe<string> | Array<InputMaybe<string>> | null,
): string[] | undefined => {
  if (!input) return undefined
  if (Array.isArray(input)) {
    return input.filter((v): v is string => !!v)
  }
  return [input]
}

export const useVendorSearchState = (): VendorSearchState => {
  const { throwAppError } = useAppError()
  const apolloClient = useApolloClient()
  const authState = useAuthState()
  const userState = useAppStateStore((state) => state.userState)
  const updateUserState = useAppStateStore((state) => state.updateUserState)
  const [previousVendors, setPreviousVendors] = useState<VendorsInRangeQuery['vendors']['rows']>([])
  const [filterOpen, toggleFilterOpen] = useState(false)
  const [urlSearchFilters, setUrlSearchFilters] = useUrlSearchFilters()
  const tenant = useTenant()
  const getFiveStarRating = useFeatureFlag(SPLITS.FIVE_STAR_RATINGS)
  const defaultUrlSearchFiltersSet = useRef(false)
  const runTimeUrlSearchFiltersSet = useRef(false)

  const [findVendors, vendorData] = useLazyQuery<VendorsInRangeQuery, VendorsInRangeQueryVariables>(
    findVendorsInRangeQuery,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
    },
  )

  const parsedUrlFiltersWithDefaults = useMemo(() => {
    return routerQuerySchemaWithDefaults.parse(urlSearchFilters)
  }, [urlSearchFilters])

  useEffect(() => {
    if (defaultUrlSearchFiltersSet.current === false) {
      defaultUrlSearchFiltersSet.current = true
      setUrlSearchFilters(parsedUrlFiltersWithDefaults, { silent: true })
    }
  }, [parsedUrlFiltersWithDefaults, setUrlSearchFilters])

  const filtersMappedFromUrl = useMemo(() => {
    return mapUrlFiltersToGraphqlFilters(parsedUrlFiltersWithDefaults, tenant, getFiveStarRating)
  }, [parsedUrlFiltersWithDefaults, tenant, getFiveStarRating])

  const address: Pick<Address, 'city' | 'street' | 'postcode' | 'latitude' | 'longitude'> | null =
    useMemo(() => {
      return userState.location
        ? basketLocationToAddress(userState.location)
        : parsedUrlFiltersWithDefaults.postcode && parsedUrlFiltersWithDefaults.street
          ? {
              postcode: parsedUrlFiltersWithDefaults.postcode,
              street: parsedUrlFiltersWithDefaults.street,
              city: parsedUrlFiltersWithDefaults.city || null,
              latitude: parsedUrlFiltersWithDefaults.lat || null,
              longitude: parsedUrlFiltersWithDefaults.lng || null,
            }
          : null
    }, [
      userState.location,
      parsedUrlFiltersWithDefaults.postcode,
      parsedUrlFiltersWithDefaults.street,
      parsedUrlFiltersWithDefaults.city,
      parsedUrlFiltersWithDefaults.lat,
      parsedUrlFiltersWithDefaults.lng,
    ])

  useEffect(() => {
    if (!address || address.latitude || address.longitude) return

    void lookupAddressDetails(address, apolloClient, true).then((addressFound) => {
      if (addressFound) {
        updateUserState({
          location: addressFound as UserBasketSettingsLocationFragment,
        })
      }
    })
  }, [address, apolloClient, updateUserState])

  // Initialize userState from URL params
  const hasInitializedFromUrl = useRef(false)

  useEffect(() => {
    if (hasInitializedFromUrl.current) return
    if (!defaultUrlSearchFiltersSet.current) return

    const { lat, lng, headCount, minDateTime, street, postcode, city, country } =
      parsedUrlFiltersWithDefaults
    const hasLocationParams = lat != null && lng != null
    const hasUrlParams = hasLocationParams || headCount > 0 || minDateTime

    if (!hasUrlParams) return

    hasInitializedFromUrl.current = true

    const updates: Partial<typeof userState> = {}

    if (headCount > 0) {
      updates.headCount = headCount
    }

    if (minDateTime) {
      const dateString = normalizeUtcDateTimeString(minDateTime)
      const parsedDate = new Date(dateString)
      if (!Number.isNaN(parsedDate.getTime())) {
        updates.date = new Date(
          parsedDate.getUTCFullYear(),
          parsedDate.getUTCMonth(),
          parsedDate.getUTCDate(),
        ).getTime()
        const hours = parsedDate.getUTCHours().toString().padStart(2, '0')
        const minutes = parsedDate.getUTCMinutes().toString().padStart(2, '0')
        const endMinutes = (parsedDate.getUTCMinutes() + 30) % 60
        const endHours =
          parsedDate.getUTCMinutes() + 30 >= 60
            ? (parsedDate.getUTCHours() + 1) % 24
            : parsedDate.getUTCHours()
        updates.time = `${hours}:${minutes} - ${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`
      }
    }

    if (hasLocationParams && (street || postcode)) {
      updates.location = {
        __typename: 'Address',
        latitude: lat,
        longitude: lng,
        street: street || null,
        postcode: postcode || null,
        city: city || null,
        country: country || null,
        label: null,
        flatOrBuilding: null,
        floorOrBuildingName: null,
      } as UserBasketSettingsLocationFragment
    }

    if (Object.keys(updates).length > 0) {
      updateUserState(updates)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedUrlFiltersWithDefaults])

  const debouncedFetch = useMemo(() => {
    return debounce((graphFilters: VendorsInRangeQueryVariables, offset: number | undefined) => {
      const variables = {
        ...graphFilters,
        offset: offset || 0,
      }
      if (vendorData.called) {
        vendorData.refetch(variables)
      } else {
        findVendors({
          variables,
        })
      }
    }, 500)
  }, [vendorData, findVendors])

  const refetchVendors = ({
    offset,
    search,
    headCount,
    keyDatesTags,
    cuisineTags,
    occasionTags,
    dietaryTags,
    highlightsTags,
    diversityOfOwnershipTags,
    bestForTags,
    minDateTime,
    orderBy,
    supplierType,
    location,
    itemSearch,
  }: Partial<VendorsInRangeQueryVariables>) => {
    const setValue: z.infer<typeof routerQuerySchemaWithDefaults> = {
      ...parsedUrlFiltersWithDefaults,
      ...(location && {
        lat: location.latitude,
        lng: location.longitude,
      }),
      ...(typeof search === 'string' && { search }),
      ...(typeof minDateTime === 'string' && {
        minDateTime: normalizeUtcDateTimeString(minDateTime),
      }),
      ...(keyDatesTags && { keyDatesTags: cleanTagArray(keyDatesTags) }),
      ...(diversityOfOwnershipTags && {
        diversityOfOwnershipTags: cleanTagArray(diversityOfOwnershipTags),
      }),
      ...(cuisineTags && { cuisineTags: cleanTagArray(cuisineTags) }),
      ...(highlightsTags && { highlightsTags: cleanTagArray(highlightsTags) }),
      ...(occasionTags && { occasionTags: cleanTagArray(occasionTags) }),
      ...(dietaryTags && { dietaryTags: cleanTagArray(dietaryTags) }),
      ...(bestForTags && { bestForTags: cleanTagArray(bestForTags) }),
      ...{ headCount: headCount ?? 0 },
      ...(orderBy?.property
        ? {
            orderByDirection: orderBy.direction || 'asc',
            orderByProperty: orderBy.property,
          }
        : {}),
      ...(supplierType === 'pantry' || supplierType === 'catering' || supplierType === 'all'
        ? { supplierType: supplierType }
        : {}),
      itemSearch:
        typeof itemSearch === 'string'
          ? itemSearch || undefined
          : parsedUrlFiltersWithDefaults.itemSearch,
    }

    const urlFilters: ParsedUrlQueryInput = {
      ...setValue,
      location: undefined,
    }

    void setUrlSearchFilters(urlFilters, { silent: true })
    const mappedFilters = mapUrlFiltersToGraphqlFilters(setValue, tenant, getFiveStarRating)
    const locationInput = mappedFilters.location

    if (
      !locationInput ||
      typeof locationInput.latitude !== 'number' ||
      typeof locationInput.longitude !== 'number'
    ) {
      setPreviousVendors([])
      return
    }

    const graphFilters: VendorsInRangeQueryVariables = {
      ...mappedFilters,
      lat: locationInput.latitude,
      lng: locationInput.longitude,
      deliveryRegionFilter: {
        serviceType: ServiceType.Gm,
        ...(mappedFilters.deliveryRegionFilter ?? {}),
        location: {
          lat: locationInput.latitude,
          lng: locationInput.longitude,
        },
      },
    }
    setPreviousVendors(
      offset
        ? Array.from(new Set([...previousVendors, ...(vendorData.data?.vendors.rows || [])]))
        : [],
    )
    debouncedFetch(graphFilters, offset ?? 0)
  }

  const defaultSearch = () => {
    const disableDefaultSearch = true
    if (disableDefaultSearch) {
      // Default search is disabled
    }
  }

  const hasInitialisedFilters = useRef(false)
  useEffect(() => {
    if (!defaultUrlSearchFiltersSet.current || hasInitialisedFilters.current) return
    hasInitialisedFilters.current = true
  }, [])

  useEffect(() => {
    if (!defaultUrlSearchFiltersSet.current) return
    const addressLocation = userState.location ? basketLocationToAddress(userState.location) : null

    let orderBy: VendorMarketplaceOrderByInput | undefined
    if (runTimeUrlSearchFiltersSet.current === false && authState.type === 'authenticated') {
      runTimeUrlSearchFiltersSet.current = true
      orderBy = {
        direction: VendorOrderDirection.Asc,
        property: VendorMarketplaceOrderByProperties.Favourites,
      }
    }

    refetchVendors({
      ...(userState.date &&
        userState.time && {
          minDateTime: userStateDateTimeToUTCString(userState.date, userState.time),
        }),
      ...(addressLocation?.latitude &&
        addressLocation.longitude && {
          location: { latitude: addressLocation.latitude, longitude: addressLocation.longitude },
        }),
      ...(orderBy && { orderBy }),
      headCount: userState.headCount ?? 0,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userState.date, userState.time, userState.location, userState.headCount, authState.type])

  const filterCount = useMemo(() => {
    const counter = Object.values({ ...filtersMappedFromUrl }).reduce(
      (sum: number, value: unknown | unknown[]) => {
        if (Array.isArray(value)) {
          return sum + value.length
        }
        return sum
      },
      0,
    )
    return counter
  }, [filtersMappedFromUrl])

  const search = (query: Partial<VendorsInRangeQueryVariables>) => {
    try {
      refetchVendors(query)
    } catch (error) {
      throwAppError({ type: 'COULD_NOT_FETCH_VENDORS' })
    }
  }

  const toggleFilterOpenHandler = () => {
    toggleFilterOpen((prev) => !prev)
  }

  if (vendorData.error) {
    throwAppError({ type: 'COULD_NOT_FETCH_VENDORS', cause: vendorData.error })
  }

  return {
    vendors: vendorData.data ? vendorData.data.vendors.rows : previousVendors,
    pageInfo: vendorData.data?.vendors.pageInfo ?? {
      count: 0,
      hasMore: false,
      limit: 0,
      offset: 0,
    },
    stats: vendorData.data?.vendors.stats || {
      dietaryTags: [],
      keyDatesTags: [],
      occasionTags: [],
      cuisineTags: [],
      diversityOfOwnershipTags: [],
      bestForTags: [],
    },
    searching: vendorData.loading,
    searchComplete: vendorData.called && !vendorData.loading,
    called: vendorData.called,
    search,
    toggleFilterOpen: toggleFilterOpenHandler,
    filterOpen,
    filters: filtersMappedFromUrl,
    filterCount,
    defaultSearch,
  }
}

export default useVendorSearchState
